/**
 * CurrencyTable Component
 * 
 * Displays currencies in a table format with actions.
 */

import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Currency } from "../../../types";
import { UseCurrencyHelpersReturn } from "../hooks/useCurrencyHelpers";
import { UseCurrencyHandlersReturn } from "../hooks/useCurrencyHandlers";
import SortableCurrencyRow from "./SortableCurrencyRow";
import { DragEndEvent } from "@dnd-kit/core";

export interface CurrencyTableProps {
  currencies: Currency[];
  defaultCurrency: string;
  helpers: UseCurrencyHelpersReturn;
  handlers: UseCurrencyHandlersReturn;
  onAddCurrencyClick: () => void;
  sensors: ReturnType<typeof import("@dnd-kit/core").useSensors>;
  onDragEnd: (event: DragEndEvent) => Promise<void>;
  isMobile?: boolean;
  /** Called when drag starts/ends so parent can disable scroll during reorder */
  onDragStateChange?: (dragging: boolean) => void;
  canAddCurrency?: boolean;
  canChangeDefaultCurrency?: boolean;
  canChangeCurrencyRateDecimalPlaces?: boolean;
}

/**
 * Table component for displaying currencies (desktop table, mobile card list)
 */

export default function CurrencyTable({
  currencies,
  defaultCurrency,
  helpers,
  handlers,
  onAddCurrencyClick,
  sensors,
  onDragEnd,
  isMobile = false,
  onDragStateChange,
  canAddCurrency = true,
  canChangeCurrencyRateDecimalPlaces = true,
}: CurrencyTableProps): React.ReactElement {
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      onDragStateChange?.(false);
      onDragEnd(event);
    },
    [onDragEnd, onDragStateChange]
  );

  const handleDragStart = React.useCallback(() => {
    onDragStateChange?.(true);
  }, [onDragStateChange]);

  const handleDragCancel = React.useCallback(() => {
    onDragStateChange?.(false);
  }, [onDragStateChange]);

  if (isMobile) {
    return (
      <>
        {currencies.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: "center",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No currencies configured. Add your first currency to get started.
            </Typography>
            {canAddCurrency && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={onAddCurrencyClick} fullWidth>
                Add Currency
              </Button>
            )}
          </Paper>
        ) : (
          <>
            {canAddCurrency && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddCurrencyClick}
                fullWidth
                sx={{
                  mt: 1,
                  mb: 1,
                  py: 1,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  bgcolor: "background.default",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                Add Currency
              </Button>
            )}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={currencies.map((c) => c.code)} strategy={verticalListSortingStrategy}>
                <Stack spacing={1}>
                  {currencies.map((currency, index) => (
                    <SortableCurrencyRow
                      key={currency.code}
                      currency={currency}
                      defaultCurrency={defaultCurrency}
                      helpers={helpers}
                      handlers={handlers}
                      index={index}
                      totalCurrencies={currencies.length}
                      isMobile
                      canAddCurrency={canAddCurrency}
                      canChangeCurrencyRateDecimalPlaces={canChangeCurrencyRateDecimalPlaces}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {currencies.length > 0 && canAddCurrency && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddCurrencyClick}
          fullWidth
          sx={{ mt: 1, mb: 2, py: 1.25 }}
        >
          Add Currency
        </Button>
      )}
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ 
          border: 1, 
          borderColor: "divider",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <Table sx={{ tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Exchange Rate</TableCell>
                <TableCell>Decimals</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Default</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currencies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No currencies configured. Add your first currency to get started.
                    </Typography>
                    {canAddCurrency && (
                      <Button variant="contained" startIcon={<AddIcon />} onClick={onAddCurrencyClick}>
                        Add Currency
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                <SortableContext items={currencies.map((c) => c.code)} strategy={verticalListSortingStrategy}>
                  {currencies.map((currency, index) => (
                    <SortableCurrencyRow
                      key={currency.code}
                      currency={currency}
                      defaultCurrency={defaultCurrency}
                      helpers={helpers}
                      handlers={handlers}
                      index={index}
                      totalCurrencies={currencies.length}
                      canAddCurrency={canAddCurrency}
                      canChangeCurrencyRateDecimalPlaces={canChangeCurrencyRateDecimalPlaces}
                    />
                  ))}
                </SortableContext>
              )}
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
    </>
  );
}
